import React, { useState, useContext, useEffect, useRef } from "react";
import { MenuContext } from "../context/MenuContext";
import { AuthContext } from "../context/AuthContext";
import OffCanvas from "../components/global/OffCanvas";
import { PartidasContext } from "../context/PartidasContext";
import { DestajosContext } from "../context/DestajosContext";
import { EditModeContext } from "../context/EditModeContext";
import { ProyectosContext } from "../context/ProyectosContext";
import { CapacitorContext } from "../context/CapacitorContext";
import ListaCotejoTabla from "../components/obra/ListaCotejoTabla";
import { SubdestajosContext } from "../context/SubdestajosContext";
import { AvanceModeContext } from "../context/AvanceModeContext";
import PartidaDetail from "../components/cronograma/PartidaDetail";
import DestajoDetail from "../components/cronograma/DestajoDetail";
import CronogramaMobile from "../components/cronograma/CronogramaMobile";
import SubdestajoDetail from "../components/cronograma/SubdestajoDetail";
import PlantillasButtons from "../components/plantillas/PlantillasButtons";
import DynamicSearchSelect from "../components/common/DynamicSearchSelect";
import EliminadosObra from "../components/obra/EliminadosObra";
import HistorialObra from "../components/obra/HistorialObra";
import { canEditPartidas } from "../utils/permisos";

const MODE_OPTIONS = [
  { value: "percentage", label: "Porcentaje" },
  { value: "amount", label: "Cantidad" },
];

const INPUT_OPTIONS = [
  { value: "acumulado", label: "Acumulado" },
  { value: "diferencial", label: "Diferencial" },
];

const Progreso = ({ idProyecto }) => {
  const [showOffCanvas, setShowOffCanvas] = useState(false);
  // Global obra-table preferences (see AvanceModeContext).
  const [mode, setMode] = useState("percentage");
  const [inputType, setInputType] = useState("acumulado");
  const { editMode, editModeOn, editModeOff, viewLevel } =
    useContext(EditModeContext);
  const { subdestajo, subdestajos, getSubDestajos } =
    useContext(SubdestajosContext);
  const { partida, partidas, getPartidasProyecto } =
    useContext(PartidasContext);
  const {
    proyecto,
    getSingleProyecto,
    setPropiedadProyecto,
    updatePreferenciaProyecto,
  } = useContext(ProyectosContext);
  const { destajo, destajos, getDestajos } = useContext(DestajosContext);
  const { platform } = useContext(CapacitorContext);
  const { setSelected } = useContext(MenuContext);
  const { user } = useContext(AuthContext);

  // Only who can edit the obra (same rule as the ObraCard options menu) changes
  // its default progress mode.
  const canChangeMode =
    ["admin", "coordinador"].includes(proyecto?.permiso) ||
    user?.permiso === "admin";
  // Contratistas also pick the mode to capture progress, but only for
  // themselves (saved in this device), not as the obra's default.
  const esContratista =
    proyecto?.permiso === "contratista" || user?.permiso === "contratista";
  const showModePickers =
    proyecto?.cantidades_enabled && (canChangeMode || esContratista);
  const modoLocalKey = `modo_progreso_${idProyecto}`;

  useEffect(() => {
    getObra();
    return handleClear;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seed the Modo/Captura pickers from the project's preferences once it loads.
  // The pickers stay editable, so this only sets the starting values.
  const seededPrefs = useRef(false);
  useEffect(() => {
    if (proyecto && !seededPrefs.current) {
      let modoLocal = null;
      try {
        modoLocal = window.localStorage.getItem(modoLocalKey);
      } catch (error) {}
      if (!canChangeMode && modoLocal) setMode(modoLocal);
      else if (proyecto.modo_progreso) setMode(proyecto.modo_progreso);
      if (proyecto.tipo_captura) setInputType(proyecto.tipo_captura);
      seededPrefs.current = true;
    }
  }, [proyecto]);

  // The obra name as the page heading, so screenshots show which obra it is.
  useEffect(() => {
    setSelected({ title: "Cronograma", heading: proyecto?.nombre });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proyecto?.nombre]);

  const handleClear = () => {
    //clearAlerts();
    editModeOff();
  };

  const getObra = () => {
    editModeOff();
    getPartidasProyecto(idProyecto);
    getSingleProyecto(idProyecto);
    getSubDestajos(idProyecto);
    getDestajos(idProyecto);
  };

  // Historial drawer (tabs: cambios / eliminados).
  const [showHistorial, setShowHistorial] = useState(false);
  const [tabHistorial, setTabHistorial] = useState("cambios");

  const renderPanel = () => (
    <OffCanvas
      title="Historial"
      showOffCanvas={showHistorial}
      setShowOffCanvas={() => setShowHistorial(false)}
      style={{ width: "min(760px, 100vw)" }}
    >
      {showHistorial && (
        <>
          <div className="btn-group w-100 mb-3">
            {[
              ["cambios", "Cambios", "fa-history"],
              ["eliminados", "Eliminados", "fa-trash"],
            ].map(([key, label, icon]) => (
              <button
                key={key}
                className={`btn ${
                  tabHistorial === key ? "btn-primary" : "btn-outline-primary"
                }`}
                onClick={() => setTabHistorial(key)}
              >
                <i className={`fa ${icon} d-inline me-2`} />
                {label}
              </button>
            ))}
          </div>
          {tabHistorial === "cambios" ? (
            <HistorialObra idProyecto={idProyecto} />
          ) : (
            <EliminadosObra idProyecto={idProyecto} callback={getObra} />
          )}
        </>
      )}
    </OffCanvas>
  );

  const handleDetail = () => {
    setShowOffCanvas(true);
  };

  const renderLista = () => {
    if (proyecto && proyecto !== null) {
      if (proyecto.permiso && proyecto.permiso !== "cliente") {
        if (platform === "web") {
          return (
            <ListaCotejoTabla
              obra={proyecto}
              proyecto={proyecto}
              partidas={partidas}
              destajos={destajos}
              editMode={editMode}
              getProyecto={getObra}
              viewLevel={viewLevel}
              editModeOn={editModeOn}
              editModeOff={editModeOff}
              subdestajos={subdestajos}
              showDetails={handleDetail}
            />
          );
        }
        return (
          <div className="row ">
            <CronogramaMobile
              partidas={partidas}
              destajos={destajos}
              getAllItems={getObra}
              idProyecto={idProyecto}
              subdestajos={subdestajos}
            />
          </div>
        );
      }
    }
  };

  const renderOffCanvasContent = () => {
    if (showOffCanvas) {
      if (partida !== null) {
        return (
          <PartidaDetail
            idProyecto={idProyecto}
            callback={getObra}
            partida={partida}
          />
        );
      }
      if (destajo !== null) {
        return (
          <DestajoDetail
            idProyecto={idProyecto}
            callback={getObra}
            destajo={destajo}
          />
        );
      }
      if (subdestajo !== null) {
        return (
          <SubdestajoDetail
            idProyecto={idProyecto}
            subdestajo={subdestajo}
            callback={getObra}
          />
        );
      }
    }
  };

  // Update a project preference in place and persist it. The pickers both seed
  // from and write back to the project's saved preference.
  const persistPref = (key, value) =>
    updatePreferenciaProyecto(proyecto?.idProyecto, key, value);

  const renderModePickers = () => (
    <div className="d-flex align-items-center gap-2 my-2">
      <div className="flex-fill" style={{ minWidth: 0 }}>
        <DynamicSearchSelect
          items={MODE_OPTIONS}
          value={mode}
          modifier={(val) => {
            const next = val || "percentage";
            setMode(next);
            if (canChangeMode) {
              persistPref("modo_progreso", next);
            } else {
              try {
                window.localStorage.setItem(modoLocalKey, next);
              } catch (error) {}
            }
          }}
          placeholder="Modo"
        />
      </div>
      {/* Captura (acumulado/diferencial) picker hidden: the obra's tipo_captura
          (ObraForm) still applies. */}
      <div className="flex-fill d-none" style={{ minWidth: 0 }}>
        <DynamicSearchSelect
          items={INPUT_OPTIONS}
          value={inputType}
          modifier={(val) => {
            const next = val || "acumulado";
            setInputType(next);
            persistPref("tipo_captura", next);
          }}
          placeholder="Captura"
        />
      </div>
    </div>
  );

  const renderContent = () => {
    if (platform === "web") {
      return (
        <div className="container-fluid">
          <div className="d-flex flex-wrap align-items-center justify-content-between py-2">
            <h4 className="bold mb-2">Cronograma</h4>
            <div className="d-flex flex-wrap align-items-center gap-2">
              {showModePickers && renderModePickers()}
              <PlantillasButtons
                idProyecto={idProyecto}
                tipo="cronograma"
                permiso={proyecto !== null ? proyecto.permiso : user.permiso}
                partidas={partidas}
              />
              {canEditPartidas(proyecto?.permiso) && (
                <button
                  className="btn btn-outline-primary mb-2"
                  onClick={() => setShowHistorial(true)}
                >
                  <i className="fa fa-history me-1"></i> Historial
                </button>
              )}
            </div>
          </div>
          {renderLista()}
          {renderPanel()}
          <OffCanvas
            showOffCanvas={showOffCanvas}
            setShowOffCanvas={setShowOffCanvas}
          >
            {renderOffCanvasContent()}
          </OffCanvas>
        </div>
      );
    } else {
      return (
        <div
          className="container-fluid px-0 h-100"
          style={{ overflowY: "scroll", overflowX: "hidden" }}
        >
          {showModePickers && (
            <div className="px-2">{renderModePickers()}</div>
          )}
          <div className="px-0">{renderLista()}</div>
          <OffCanvas
            showOffCanvas={showOffCanvas}
            setShowOffCanvas={setShowOffCanvas}
          >
            {renderOffCanvasContent()}
          </OffCanvas>
        </div>
      );
    }
  };

  return (
    <AvanceModeContext.Provider
      value={{
        // Amount mode is only available when the project enables cantidades;
        // otherwise everything stays percentage and the pickers are hidden.
        mode: proyecto?.cantidades_enabled ? mode : "percentage",
        setMode,
        inputType,
        setInputType,
      }}
    >
      {renderContent()}
    </AvanceModeContext.Provider>
  );
};

export default Progreso;
